import { ReactNode, useCallback, useState } from 'react'
import { tileEntityByIdSelector } from '~/app/store/tileEntity'
import { useSelector } from 'react-redux'
import { colorSwatchSelector, designByIdSelector } from '~/app/store/design'
import { isPreviouslyOrderedTemplate } from '~/shared/sets/previouslyOrderedTemplate'
import { AnalyticsNames, ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL, PREVIEW_TYPE } from 'shared/constants'
import { resolveInitialSwatchId } from 'shared/resolveInitialSwatchId'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { getBypassApproval } from '~/app/store/debug'
import { galleryImpressionIdSelector } from '~/app/store/analytics/reducer'
import { useGreetingDescription } from '~/app/features/Gallery/hooks/useGreetingDescription/useGreetingDescription'
import { useSocialMediaGallery } from '~/app/features/Gallery/hooks/useSocialMediaGallery'
import { designPersonalizationContextIdSelector } from '~/app/store/designPersonalization/selectors'
import { useNewTemplateAltText } from '~/app/features/Gallery/hooks/useNewTemplateAltText'
import { NUMBER_OF_IMAGES_WITH_HIGH_PRIORITY } from '~/app/features/Gallery/components/PreviewArea/constants'
import { BaseImageProps } from '~/app/features/Gallery/components/DesignTile/interface'
import { usePrefetchQuery } from '~/app/features/Gallery/components/PreviewArea/hooks/usePrefetchQuery'

export type PropTypes = {
  tileEntityId: string
  children: (
    tileEntity: State.TileEntity,
    imageProps: BaseImageProps,
    handleColorSwatchChange: (newDesignId: string) => void,
    colorSwatchObjects: Gallery.ContentQuery.ColorSwatch[],
    currentColorSwatch: Gallery.Designs.CurrentColorSwatch,
    currentDesignId: string,
    greetingOptions: string,
    greetingTitle: string,
    shouldShowGreetingOptions: boolean,
    shouldHideForSocialMedia: boolean,
    isPreviouslyOrdered: boolean,
    onMouseEnter?: () => void,
  ) => ReactNode | null
}

function createTileAnalyticsData(tileEntity: State.TileEntity): Gallery.Analytics.DesignAnalyticsObject {
  return {
    tileEntity,
    position: tileEntity.position,
    location: AnalyticsNames.DesignTile,
  }
}

export const GalleryTileEntityProvider = (props: PropTypes) => {
  const { tileEntityId, children } = props
  const tileEntityById = useSelector(tileEntityByIdSelector)
  const selectColorSwatch = useSelector(colorSwatchSelector)
  const analytics = useAnalytics()
  const designById = useSelector(designByIdSelector)
  const tileEntity = tileEntityById(tileEntityId)
  const initialSwatchId = resolveInitialSwatchId(tileEntity)
  const [localDesignId, setLocalDesignId] = useState<string | null>(null)
  const currentDesignId = localDesignId && designById(localDesignId) ? localDesignId : initialSwatchId
  const shouldHideForSocialMedia = useSocialMediaGallery()
  const dpcId = useSelector(designPersonalizationContextIdSelector)
  const isPreviouslyOrdered = isPreviouslyOrderedTemplate(tileEntity.rankingSet)

  const { greetingTitle, greetingOptions, shouldShowGreetingOptions } = useGreetingDescription(tileEntity)

  const currentColorSwatch = selectColorSwatch(currentDesignId, tileEntityId, PREVIEW_TYPE.TILE)

  const previewAlt = useNewTemplateAltText(tileEntity, currentColorSwatch.colorSwatches)
  const onMouseEnter = usePrefetchQuery(tileEntity)
  const bypassedApproval = useSelector(getBypassApproval)
  const impressionId = useSelector((state: State.GlobalState) => galleryImpressionIdSelector(state, tileEntityId))

  const { previewsUrls } = currentColorSwatch.renderablePreviews

  const imageProps: BaseImageProps = {
    alt: previewAlt,
    previewsUrls,
    bypassedApproval,
  }

  if (tileEntity.position < NUMBER_OF_IMAGES_WITH_HIGH_PRIORITY) {
    imageProps.fetchpriority = 'high'
  }

  const handleColorSwatchChange = useCallback(
    (newDesignId: string): void => {
      const color = designById(newDesignId).color?.split('#')[1]
      const analyticsData = createTileAnalyticsData(tileEntity)
      const colorSwatchPersonalizedDetail = dpcId ? 'Tile color selection with personalized design' : ''

      analytics.trackEvent({
        action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
        eventLabel: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
        eventDetail: colorSwatchPersonalizedDetail,
        ...analytics.buildDesignEngagement({
          engagementAction: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
          selectedDesign: newDesignId,
          color,
          tileEntity: analyticsData.tileEntity,
          colorSwatchObjects: currentColorSwatch.colorSwatches,
          impressionId,
          location: analyticsData.location,
          position: analyticsData.tileEntity.position,
        }),
      })

      setLocalDesignId(newDesignId)
    },
    [designById, tileEntity, dpcId, analytics, currentColorSwatch.colorSwatches, impressionId],
  )

  return children(
    tileEntity,
    imageProps,
    handleColorSwatchChange,
    currentColorSwatch.colorSwatches,
    currentColorSwatch,
    currentDesignId,
    greetingOptions,
    greetingTitle,
    shouldShowGreetingOptions,
    shouldHideForSocialMedia,
    isPreviouslyOrdered,
    onMouseEnter,
  )
}
